import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetWellsQuery } from "@api/mApi";
import { useGetAllEventsQuery } from "@api/pApi";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectWell, setWellQuery } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { WellMap } from "@components/map/WellMap";
import { QueryState } from "@components/ui/QueryState";
import { MultiSelectDropdown, type MultiSelectOption } from "@components/ui/MultiSelectDropdown";
import { fmtDepth } from "@lib/format";
import { isGlobalAdmin } from "@api/brokerAuth";
import {
  STATUS_KEY as STATUS_MSG,
  eventTypeLabel,
} from "@features/wellRecord/wellLabels";
import { NewWellButton } from "./NewWellButton";
import type { EventOut, WellView, WellStatus } from "@api/types";

/** The known well-status values, in display order, for the Status filter. */
const STATUS_VALUES: WellStatus[] = ["drilling", "completion", "producing", "suspended"];

/**
 * The most recent activity date for a well = max(date_ops_end ?? date_ops_start)
 * across its events. Returns null when the well has no dated events (or no
 * events at all). Used to sort the register by recency, newest first.
 */
function lastEventTime(events: EventOut[] | undefined): number | null {
  if (!events || events.length === 0) return null;
  let max: number | null = null;
  for (const ev of events) {
    const raw = ev.date_ops_end ?? ev.date_ops_start;
    if (!raw) continue;
    const ts = Date.parse(raw);
    if (Number.isNaN(ts)) continue;
    if (max === null || ts > max) max = ts;
  }
  return max;
}

function uniq(xs: string[]): string[] {
  return Array.from(new Set(xs.filter((x) => x && x !== "—"))).sort();
}

/**
 * The compact label for one event chip: prefer the concrete event_code
 * (WOV / ODR / OCM / …); fall back to the coarser event_type when a code is
 * absent so a chip is never blank.
 */
function eventChipLabel(ev: EventOut): string {
  return (ev.event_code || ev.event_type || "").toUpperCase();
}

/**
 * Well Explorer — the proof-of-stack screen. Spatial-first: a real Leaflet map
 * beside a filterable register table. Reads the well register from Oxbo-M
 * (served by MSW in standalone mode, or the live m.dev service). The M contract
 * filters server-side on `status`; the finer field/basin/operator/search
 * filters are applied client-side over the register (which is small and cached).
 * Clicking a well selects it and drops into its record.
 */
export function WellExplorer() {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  // The well-search text is shared with the top switcher (redux), so typing in
  // either box filters the Explorer list + map live.
  const search = useAppSelector((s) => s.wellContext.wellQuery);

  // Multi-select filters: each is a Set of selected values; an empty set means
  // "no filter" (all wells pass). AND across filters, OR within a filter.
  const [fieldSel, setFieldSel] = useState<Set<string>>(new Set());
  const [basinSel, setBasinSel] = useState<Set<string>>(new Set());
  const [operatorSel, setOperatorSel] = useState<Set<string>>(new Set());
  const [statusSel, setStatusSel] = useState<Set<string>>(new Set());
  const [eventTypeSel, setEventTypeSel] = useState<Set<string>>(new Set());
  // Default on: most wells in the register have never had a job — start focused
  // on the ~119 wells that have operational activity.
  const [onlyWithEvents, setOnlyWithEvents] = useState(true);

  // The register itself is fetched unfiltered (small, cached); we derive filter
  // option lists from it and apply all filters client-side for instant feedback.
  const { data: allWells = [], isLoading, isError, refetch } = useGetWellsQuery();

  // Tenant-wide events, fetched once and grouped by well UWI. The join key is
  // WellView.uwi === event.well_uwi.
  const { data: allEvents = [] } = useGetAllEventsQuery();
  const eventsByWell = useMemo(() => {
    const map = new Map<string, EventOut[]>();
    for (const ev of allEvents) {
      const key = ev.well_uwi;
      if (!key) continue;
      const bucket = map.get(key);
      if (bucket) bucket.push(ev);
      else map.set(key, [ev]);
    }
    return map;
  }, [allEvents]);

  const fields = useMemo(() => uniq(allWells.map((w) => w.field)), [allWells]);
  const basins = useMemo(() => uniq(allWells.map((w) => w.basin)), [allWells]);
  const operators = useMemo(() => uniq(allWells.map((w) => w.operator)), [allWells]);

  // Status options: the known status values that actually occur in the register,
  // presented as {value, localizedLabel} so the checkbox list reads naturally.
  const statusOptions = useMemo<MultiSelectOption[]>(() => {
    const present = new Set(allWells.map((w) => w.status).filter(Boolean) as WellStatus[]);
    return STATUS_VALUES.filter((s) => present.has(s)).map((s) => ({
      value: s,
      label: t(STATUS_MSG[s]),
    }));
  }, [allWells, t]);

  // Event-type options: the distinct event_type values present across all
  // events, with EN/ES/PT display labels. Value stays the raw type so the
  // filter matches events regardless of the active locale.
  const eventTypeOptions = useMemo<MultiSelectOption[]>(() => {
    const seen = new Set<string>();
    for (const bucket of eventsByWell.values()) {
      for (const ev of bucket) if (ev.event_type) seen.add(ev.event_type);
    }
    return Array.from(seen)
      .sort()
      .map((et) => ({ value: et, label: eventTypeLabel(et, t) }));
  }, [eventsByWell, t]);

  const wells = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = allWells.filter((w) => {
      if (q && !`${w.commonName} ${w.uwi} ${w.apiNo ?? ""} ${w.field}`.toLowerCase().includes(q))
        return false;
      if (fieldSel.size && !fieldSel.has(w.field)) return false;
      if (basinSel.size && !basinSel.has(w.basin)) return false;
      if (operatorSel.size && !operatorSel.has(w.operator)) return false;
      if (statusSel.size && !(w.status && statusSel.has(w.status))) return false;
      const wellEvents = eventsByWell.get(w.uwi);
      if (onlyWithEvents && !wellEvents) return false;
      // Event-type filter: the well must have ≥1 event whose type is selected.
      if (eventTypeSel.size) {
        if (!wellEvents || !wellEvents.some((ev) => eventTypeSel.has(ev.event_type)))
          return false;
      }
      return true;
    });

    // Sort by most-recent event date, DESC. Wells with events sort ahead of
    // wells without; ties (and event-less wells) fall back to name asc. The
    // same ordered collection feeds both the table and the map.
    return filtered.slice().sort((a, b) => {
      const ta = lastEventTime(eventsByWell.get(a.uwi));
      const tb = lastEventTime(eventsByWell.get(b.uwi));
      if (ta !== tb) {
        if (ta === null) return 1;
        if (tb === null) return -1;
        return tb - ta;
      }
      return a.commonName.localeCompare(b.commonName);
    });
  }, [
    allWells,
    search,
    fieldSel,
    basinSel,
    operatorSel,
    statusSel,
    eventTypeSel,
    onlyWithEvents,
    eventsByWell,
  ]);

  const openWell = (w: WellView) => {
    dispatch(selectWell(w.id));
    navigate("/well");
  };

  const clearFilters = () => {
    dispatch(setWellQuery(""));
    setFieldSel(new Set());
    setBasinSel(new Set());
    setOperatorSel(new Set());
    setStatusSel(new Set());
    setEventTypeSel(new Set());
  };

  return (
    <div>
      <div className="page-head">
        <div className="page-head-titles">
          <h1>{t("explorer.title")}</h1>
          <p className="subtitle">{t("explorer.subtitle")}</p>
        </div>
        {isGlobalAdmin() ? <NewWellButton /> : null}
      </div>

      {/* Data-lineage banner: hidden unless the admin overlay (.lineage-on) is set. */}
      <div className="lineage-banner" role="note">
        <span aria-hidden="true">●</span>
        {t("topbar.lineage")} — {t("topbar.lineage.adminOnly")}
      </div>

      <div className="filterbar">
        <div className="field" style={{ flex: "1 1 220px" }}>
          <label htmlFor="wx-search">{t("explorer.filter.field")}</label>
          <input
            id="wx-search"
            type="search"
            placeholder={t("explorer.search")}
            value={search}
            onChange={(e) => dispatch(setWellQuery(e.target.value))}
            aria-label={t("explorer.search")}
          />
        </div>
        <MultiSelectDropdown
          label={t("explorer.filter.field")}
          options={fields}
          selected={fieldSel}
          onChange={setFieldSel}
        />
        <MultiSelectDropdown
          label={t("explorer.filter.basin")}
          options={basins}
          selected={basinSel}
          onChange={setBasinSel}
        />
        <MultiSelectDropdown
          label={t("explorer.filter.operator")}
          options={operators}
          selected={operatorSel}
          onChange={setOperatorSel}
        />
        <MultiSelectDropdown
          label={t("explorer.filter.status")}
          options={statusOptions}
          selected={statusSel}
          onChange={setStatusSel}
        />
        <MultiSelectDropdown
          label={t("explorer.filter.eventType")}
          options={eventTypeOptions}
          selected={eventTypeSel}
          onChange={setEventTypeSel}
        />
        <label className="field events-toggle">
          <input
            type="checkbox"
            className="brand-check"
            checked={onlyWithEvents}
            onChange={(e) => setOnlyWithEvents(e.target.checked)}
          />
          {t("explorer.filter.onlyWithEvents")}
        </label>
        <span className="result-count">{t("explorer.count", { n: wells.length })}</span>
      </div>

      <div className="explorer-split">
        <div className="map-card">
          <WellMap
            wells={wells}
            selectedId={selectedWellId ?? undefined}
            onSelect={openWell}
            ariaLabel={t("explorer.map.placeholder")}
          />
        </div>

        <div className="card" style={{ padding: 0 }}>
          <div className="table-scroll">
            <QueryState
              isLoading={isLoading}
              isError={isError}
              isEmpty={wells.length === 0}
              emptyMessage={t("explorer.empty")}
              onRetry={refetch}
            >
              <table>
                <thead>
                  <tr>
                    <th>{t("explorer.col.well")}</th>
                    <th>{t("explorer.col.field")}</th>
                    <th>{t("explorer.col.operator")}</th>
                    <th>{t("explorer.col.status")}</th>
                    <th className="num">{t("explorer.col.td")}</th>
                    <th>{t("explorer.col.events")}</th>
                  </tr>
                </thead>
                <tbody>
                  {wells.map((w) => (
                    <tr
                      key={w.id}
                      className="selectable"
                      data-owner="m"
                      onClick={() => openWell(w)}
                    >
                      <td>
                        <span className="well-link">{w.commonName}</span>
                        <div className="mono" style={{ fontSize: 11, color: "var(--dim)" }}>
                          {w.apiNo ?? w.uwi}
                        </div>
                      </td>
                      <td>{w.field}</td>
                      <td>{w.operator}</td>
                      <td>
                        {w.status ? (
                          <span className={`badge status-${w.status}`}>
                            {t(STATUS_MSG[w.status])}
                          </span>
                        ) : (
                          <span className="badge">{w.statusRaw ?? "—"}</span>
                        )}
                      </td>
                      <td className="num tab-nums">{fmtDepth(w.totalDepthM, locale)}</td>
                      <td>
                        <EventsCell events={eventsByWell.get(w.uwi)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </QueryState>
          </div>
        </div>
      </div>

      <button type="button" className="btn" style={{ marginTop: 12 }} onClick={clearFilters}>
        {t("explorer.clearFilters")}
      </button>
    </div>
  );
}

/**
 * Compact per-well events cell: the event count followed by the distinct
 * codes (or types) as small chips, e.g. "3 · WOV WSV ODR". Empty when the well
 * has no events. Order is preserved by first appearance.
 */
function EventsCell({ events }: { events?: EventOut[] }) {
  if (!events || events.length === 0) return null;
  const codes: string[] = [];
  for (const ev of events) {
    const label = eventChipLabel(ev);
    if (label && !codes.includes(label)) codes.push(label);
  }
  return (
    <span className="events-cell">
      <span className="events-count tab-nums">{events.length}</span>
      {codes.map((c) => (
        <span key={c} className="badge event-chip">
          {c}
        </span>
      ))}
    </span>
  );
}
